import { Fragment, useCallback, useEffect, useEffectEvent, useRef, useState, type Ref, type RefCallback } from "react";
import type {
  EmailStatus,
  ProofLookupPayload,
  ProofLookupResponse,
  ProofRecord,
  ProofRespondResponse,
  ProofVerdict,
} from "./types";
import { ProofEditPanel } from "./proof-editor";
import { BioBox } from "./bio-box";

type Phase =
  | { kind: "asking" }
  | { kind: "loading" }
  | { kind: "error"; reason: string; message: string }
  | { kind: "link-requested" }
  | { kind: "proof"; proof: ProofRecord }
  | { kind: "done"; verdict: ProofVerdict | "edit"; emailStatus: EmailStatus; duplicate: boolean };

interface ProofViewProps {
  onBack: () => void;
  turnstileSiteKey?: string;
  turnstileToken: string;
  turnstileError: string | null;
  turnstileRef: RefCallback<HTMLElement>;
  resetTurnstile: () => void;
}

// The widget is invisible (interaction-only) when no challenge is needed, so
// never tell the writer to complete something they cannot see.
const VERIFICATION_PENDING_MESSAGE = "Verification is still running — give it a moment, then try again.";

const PROOF_TOKEN_STORAGE_KEY = "mws-proof-token";

/** Read the token a resolved proof left behind, so a refresh re-resolves. */
export function storedProofToken(): string | null {
  try {
    return window.sessionStorage.getItem(PROOF_TOKEN_STORAGE_KEY);
  } catch {
    // Storage can be blocked (private modes, cookie settings); treat as absent.
    return null;
  }
}

function rememberProofToken(token: string): void {
  try {
    window.sessionStorage.setItem(PROOF_TOKEN_STORAGE_KEY, token);
  } catch {
    // Without storage a refresh falls back to the ?k= URL, which stays intact
    // only until clearProofToken runs — acceptable degradation, not an error.
  }
}

function forgetProofToken(): void {
  try {
    window.sessionStorage.removeItem(PROOF_TOKEN_STORAGE_KEY);
  } catch {
    // Nothing was stored if storage is blocked.
  }
}

function clearProofToken(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("k");
    window.history.replaceState({}, "", url.toString());
  } catch {
    // Preserve a working proof if the History API is unavailable.
  }
}

function confirmationCopy(emailStatus: EmailStatus, duplicate: boolean): string {
  if (duplicate) return "This response was already recorded, so we didn't send another confirmation email.";
  if (emailStatus === "sent") return "A confirmation is on its way to your inbox.";
  if (emailStatus === "failed") return "Your response was recorded, but we couldn't send the confirmation email.";
  return "Your response was recorded. Email confirmation is not configured in this environment.";
}

export function ProofView({
  onBack,
  turnstileSiteKey,
  turnstileToken,
  turnstileError,
  turnstileRef,
  resetTurnstile,
}: ProofViewProps) {
  const [phase, setPhase] = useState<Phase>({ kind: "asking" });
  const [issueName, setIssueName] = useState<string>("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  // "Report a layout problem" first asks whether they could just make the change themselves (most can).
  const [nudging, setNudging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Leaving proof mode must always drop the remembered token — otherwise the
  // next refresh in this tab re-enters proof mode with no way out.
  const exitProof = useCallback(() => {
    forgetProofToken();
    onBack();
  }, [onBack]);

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const noteRef = useRef<HTMLTextAreaElement | null>(null);
  const approveButtonRef = useRef<HTMLButtonElement | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
  const responseAttemptRef = useRef<{ fingerprint: string; id: string } | null>(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Check Your Proof | Method Writers Speak";
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    if (phase.kind === "loading") return;
    const frame = window.requestAnimationFrame(() => headingRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [phase]);

  useEffect(() => {
    if (showNote) noteRef.current?.focus();
  }, [showNote]);

  useEffect(() => {
    if (confirming) confirmButtonRef.current?.focus();
  }, [confirming]);

  useEffect(() => {
    if (busy || !formError) return;
    if (confirming) confirmButtonRef.current?.focus();
    else if (showNote) noteRef.current?.focus();
  }, [busy, confirming, formError, showNote]);

  useEffect(() => {
    if (turnstileToken && formError === VERIFICATION_PENDING_MESSAGE) {
      setFormError(null);
    }
  }, [formError, turnstileToken]);

  const lookup = useCallback(async (body: Pick<ProofLookupPayload, "email"> | Pick<ProofLookupPayload, "token">) => {
    const requestingLink = "email" in body;
    setPhase({ kind: "loading" });
    setFormError(null);
    try {
      const res = await fetch("/api/proof/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...body,
          website,
          turnstileToken: requestingLink ? turnstileToken : "",
        }),
      });
      const data = (await res.json()) as ProofLookupResponse;
      if (!data.ok) {
        setPhase({ kind: "error", reason: data.reason, message: data.errors[0] ?? "Something went wrong." });
      } else if (data.kind === "proof") {
        // Remember the token before stripping it from the address bar, so a
        // refresh (or a blank iOS PDF embed prompting one) doesn't strand the
        // writer back at the welcome modal.
        rememberProofToken(data.proof.token);
        clearProofToken();
        setPhase({ kind: "proof", proof: data.proof });
        setIssueName(data.proof.issueName ?? "");
      } else {
        setPhase({ kind: "link-requested" });
      }
    } catch {
      setPhase({ kind: "error", reason: "invalid", message: "We couldn't reach the server. Please try again." });
    } finally {
      if (requestingLink) resetTurnstile();
    }
  }, [resetTurnstile, turnstileToken, website]);

  const lookupToken = useEffectEvent((token: string) => {
    void lookup({ token });
  });

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("k") ?? storedProofToken();
    if (token) lookupToken(token);
    // Failed lookups deliberately keep the token in the URL so refresh remains
    // a valid retry.
  }, []);

  const respond = async (proof: ProofRecord, verdict: ProofVerdict) => {
    const trimmedNote = note.trim();
    if (verdict === "fix" && trimmedNote.length < 10) {
      setFormError("Please quote the line as printed and say what it should read.");
      noteRef.current?.focus();
      return;
    }

    const fingerprint = `${verdict}\0${trimmedNote}`;
    if (responseAttemptRef.current?.fingerprint !== fingerprint) {
      responseAttemptRef.current = { fingerprint, id: crypto.randomUUID() };
    }
    const attemptId = responseAttemptRef.current.id;

    setBusy(true);
    setFormError(null);
    try {
      const res = await fetch("/api/proof/respond", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: proof.token, verdict, note: trimmedNote, attemptId }),
      });
      const data = (await res.json()) as ProofRespondResponse;
      if (data.ok) {
        responseAttemptRef.current = null;
        setPhase({
          kind: "done",
          verdict: data.verdict,
          emailStatus: data.emailStatus,
          duplicate: data.duplicate === true,
        });
      } else if (data.reason === "closed") {
        responseAttemptRef.current = null;
        setConfirming(false);
        setShowNote(false);
        setPhase({ kind: "proof", proof: { ...proof, closed: true } });
      } else {
        if (res.status === 409) responseAttemptRef.current = null;
        setFormError(data.errors[0] ?? "We couldn't record that. Please try again.");
      }
    } catch {
      setFormError("We couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFormError("Enter a valid email address.");
      return;
    }
    if (turnstileSiteKey && !turnstileToken) {
      setFormError(VERIFICATION_PENDING_MESSAGE);
      return;
    }
    void lookup({ email: email.trim() });
  };

  return (
    <main className="page proof-page">
      <div className="proof-shell">
        <div className="proof-kicker">{issueName ? issueName.replace(/\s*#(\d+)$/, " · Issue $1") : "Method Writers Speak"}</div>

        {phase.kind === "asking" && (
          <ProofEmailForm
            email={email}
            setEmail={setEmail}
            website={website}
            setWebsite={setWebsite}
            onSubmit={submitEmail}
            error={formError}
            emailInvalid={formError === "Enter a valid email address."}
            onBack={exitProof}
            headingRef={headingRef}
            turnstileSiteKey={turnstileSiteKey}
            turnstileError={turnstileError}
            turnstileRef={turnstileRef}
          />
        )}

        {phase.kind === "loading" && (
          <p className="proof-status" role="status" aria-live="polite">Finding your proof…</p>
        )}

        {phase.kind === "error" && (
          <div className="proof-message">
            <h1 ref={headingRef} tabIndex={-1} className="proof-title">
              {phase.reason === "hold"
                ? "Not quite yet"
                : phase.reason === "closed"
                  ? "This round has closed"
                  : phase.reason === "notfound"
                    ? "We couldn't find that"
                    : "Something went wrong"}
            </h1>
            <p className="proof-body">{phase.message}</p>
            <button
              type="button"
              className="proof-link"
              onClick={phase.reason === "closed" ? exitProof : () => { setPhase({ kind: "asking" }); setFormError(null); }}
            >
              {phase.reason === "closed" ? "Back to start" : "Try another email address"}
            </button>
          </div>
        )}

        {phase.kind === "link-requested" && (
          <div className="proof-message">
            <h1 ref={headingRef} tabIndex={-1} className="proof-title">Check your inbox</h1>
            <p className="proof-body">
              If that address matches an available proof, a private link is on its way. It may take a few minutes to arrive.
            </p>
            <button type="button" className="proof-link" onClick={exitProof}>Back to start</button>
          </div>
        )}

        {phase.kind === "done" && (
          <div className="proof-message">
            <h1 ref={headingRef} tabIndex={-1} className="proof-title">
              {phase.verdict === "ok" ? "Approved — thank you." : phase.verdict === "edit" ? "Your changes are in." : "We've got it."}
            </h1>
            <p className="proof-body">
              {phase.verdict === "ok"
                ? "Your piece runs exactly as you saw it. Nothing further is needed from you."
                : phase.verdict === "edit"
                  ? "An editor will set your new version before the issue goes to print. If you think of something else, open your link again and send a new version — the newest one is the one we use."
                  : "Your correction is logged and an editor will look at it before the issue goes to print."}
            </p>
            <p className="proof-body proof-body--muted">{confirmationCopy(phase.emailStatus, phase.duplicate)}</p>
            <button type="button" className="proof-link" onClick={exitProof}>Back to start</button>
          </div>
        )}

        {phase.kind === "proof" && (
          <>
            <h1 ref={headingRef} tabIndex={-1} className="proof-title">
              Your proof, <em>{phase.proof.first}</em>
            </h1>
            {phase.proof.titles.length > 0 && (
              <p className="proof-titles">
                {phase.proof.titles.map((title: string, index: number) => (
                  <Fragment key={title}>
                    {index > 0 && " · "}<em>{title}</em>
                  </Fragment>
                ))}
              </p>
            )}

            {phase.proof.answered && (
              <p className="proof-answered" role="status">
                {phase.proof.answered === "ok"
                  ? <>You already told us this proof was <strong>correct</strong>.</>
                  : <>You already sent us <strong>{phase.proof.editable ? "changes or a correction" : "a correction"}</strong>.</>}{" "}
                Answering again replaces it.
              </p>
            )}

            <div className="proof-viewer">
              <object data={phase.proof.fileUrl} type="application/pdf" className="proof-object" aria-label="Your typeset proof">
                <p className="proof-fallback">
                  Your browser can't show the PDF inline.{" "}
                  <a href={phase.proof.fileUrl} target="_blank" rel="noreferrer">Open it in a new tab</a>.
                </p>
              </object>
            </div>
            {/* Always visible: iOS renders <object> PDFs unreliably without
                triggering the fallback above, so the escape hatch can't live
                only inside it. */}
            <p className="proof-open-link">
              <a className="proof-link" href={phase.proof.fileUrl} target="_blank" rel="noreferrer">
                Open the PDF in a new tab
              </a>
            </p>

            <div className="proof-instructions">
              <p><strong>This is the last time you will see it before it prints.</strong></p>
              <p>
                Check that we set your work correctly — that we didn't drop a line, break a stanza in the
                wrong place, lose your italics, or misspell your name.
              </p>
              {phase.proof.editable ? (
                <p className="proof-body--muted">
                  Want to change something in your writing? Press <strong>Make changes</strong> and edit it
                  right here. The only rule is that it still fits your page. Changes close{" "}
                  <strong>{phase.proof.deadlineLabel}</strong>.
                </p>
              ) : (
                <p className="proof-body--muted">
                  Please don't use this pass to change your writing. The issue is laid out, and one changed
                  word can reflow a page, so this round is only for errors we introduced. Corrections close{" "}
                  <strong>{phase.proof.deadlineLabel}</strong>.
                </p>
              )}
            </div>

            {phase.proof.closed && (
              <p className="proof-answered" role="status">
                <strong>This proof round is closed.</strong> You can still review the PDF, but responses are no longer accepted.
              </p>
            )}

            {(phase.proof.bio.needed || phase.proof.bio.sent) && (
              <section className="proof-bio" aria-labelledby="proof-bio-title">
                <h2 id="proof-bio-title" className="proof-subtitle">Your bio</h2>
                {!phase.proof.bio.sent && (
                  <div className="bio-warning" role="status">
                    <p><strong>We still need a bio from you</strong> for the contributors' pages. You can't approve your proof until
                      you've sent one, and without it your work might be left out of the issue.</p>
                  </div>
                )}
                <BioBox auth={{ token: phase.proof.token }} name={phase.proof.name} bio={phase.proof.bio}
                  onSaved={(bio) => { setFormError(null); setPhase({ kind: "proof", proof: { ...phase.proof, bio: { ...phase.proof.bio, sent: bio } } }); }} />
              </section>
            )}

            {!phase.proof.closed && editing && (
              <ProofEditPanel
                proof={phase.proof}
                onCancel={() => { setEditing(false); setFormError(null); }}
                onSent={(emailStatus, duplicate) => {
                  setEditing(false);
                  setPhase({ kind: "done", verdict: "edit", emailStatus, duplicate });
                }}
                onClosed={() => {
                  setEditing(false);
                  setPhase({ kind: "proof", proof: { ...phase.proof, closed: true } });
                }}
              />
            )}

            {!phase.proof.closed && nudging && (
              <div className="proof-confirm proof-nudge" role="dialog" aria-labelledby="proof-nudge-title">
                <h2 id="proof-nudge-title" className="proof-subtitle">Are you sure? You can probably fix this yourself.</h2>
                <p className="proof-body">
                  Nine times out of ten, a change can be made right now, by you: a word or spelling, a line break, a stanza
                  break, italics or bold. Press <strong>Make changes</strong> and edit your piece directly. It goes in exactly
                  as you make it, and it's much faster than describing it to us.
                </p>
                <p className="proof-body proof-body--muted">
                  Only report a layout problem for something the editor can't do, such as spacing on the page or where a
                  page breaks.
                </p>
                <div className="proof-actions">
                  <button type="button" className="proof-btn proof-btn--ok" onClick={() => { setNudging(false); setEditing(true); }}>
                    Make the change myself
                  </button>
                  <button type="button" className="proof-link" onClick={() => { setNudging(false); setShowNote(true); }}>
                    It's a layout problem, report it
                  </button>
                </div>
              </div>
            )}

            {!phase.proof.closed && !showNote && !confirming && !editing && !nudging && (
              <div className="proof-actions">
                <button ref={approveButtonRef} type="button" className="proof-btn proof-btn--ok" disabled={busy || (phase.proof.bio.needed && !phase.proof.bio.sent)}
                  title={phase.proof.bio.needed && !phase.proof.bio.sent ? "Send your bio first (above)" : undefined}
                  onClick={() => { setFormError(null); setConfirming(true); }}>
                  Looks right — run it
                </button>
                {phase.proof.editable && (
                  <button type="button" className="proof-btn proof-btn--edit" disabled={busy} onClick={() => { setFormError(null); setEditing(true); }}>
                    Make changes
                  </button>
                )}
                <button type="button" className="proof-btn proof-btn--fix" disabled={busy} onClick={() => { setFormError(null); if (phase.proof.editable) setNudging(true); else setShowNote(true); }}>
                  {phase.proof.editable ? "Report a layout problem" : "Something's wrong"}
                </button>
                {phase.proof.bio.needed && !phase.proof.bio.sent && (
                  <p className="proof-hint bio-gate">Send your bio above, then you can approve.</p>
                )}
              </div>
            )}

            {!phase.proof.closed && confirming && (
              <div className="proof-confirm">
                <p id="proof-confirm-copy" className="proof-body" role="status">This approves your piece as printed. We won't ask again.</p>
                <div className="proof-actions">
                  <button ref={confirmButtonRef} type="button" className="proof-btn proof-btn--ok" disabled={busy} aria-describedby="proof-confirm-copy" onClick={() => void respond(phase.proof, "ok")}>
                    {busy ? "Recording…" : "Yes, approve it"}
                  </button>
                  <button type="button" className="proof-link" disabled={busy} onClick={() => {
                    setConfirming(false);
                    setFormError(null);
                    window.requestAnimationFrame(() => approveButtonRef.current?.focus());
                  }}>
                    Wait, go back
                  </button>
                </div>
              </div>
            )}

            {!phase.proof.closed && showNote && (
              <div className="proof-fix">
                <label className="proof-label" htmlFor="proof-note">What did we get wrong?</label>
                <p id="proof-note-hint" className="proof-hint">
                  Quote the exact line as printed, then tell us what it should say. "Stanza 3 shouldn't
                  break after 'the door'" is something we can act on. "The spacing looks off" is not.
                  If you can, paste the whole piece with the correction already made — that's the fastest
                  and safest way for us to fix it.
                </p>
                <textarea
                  id="proof-note"
                  ref={noteRef}
                  className="proof-textarea"
                  rows={9}
                  required
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Line 4 reads 'the cold water' — it should read 'the old water'."
                  aria-invalid={formError ? true : undefined}
                  aria-describedby={formError ? "proof-note-hint proof-note-error" : "proof-note-hint"}
                />
                {formError && <p id="proof-note-error" className="proof-error" role="alert">{formError}</p>}
                <div className="proof-actions">
                  <button type="button" className="proof-btn proof-btn--fix" disabled={busy} onClick={() => void respond(phase.proof, "fix")}>
                    {busy ? "Sending…" : "Send this correction"}
                  </button>
                  <button type="button" className="proof-link" disabled={busy} onClick={() => { setShowNote(false); setFormError(null); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {formError && !showNote && <p className="proof-error" role="alert">{formError}</p>}

            <p className="proof-exit">
              <button type="button" className="proof-link" onClick={exitProof}>← Back to the main site</button>
            </p>
          </>
        )}
      </div>
    </main>
  );
}

function ProofEmailForm({
  email,
  setEmail,
  website,
  setWebsite,
  onSubmit,
  error,
  emailInvalid,
  onBack,
  headingRef,
  turnstileSiteKey,
  turnstileError,
  turnstileRef,
}: {
  email: string;
  setEmail: (value: string) => void;
  website: string;
  setWebsite: (value: string) => void;
  onSubmit: () => void;
  error: string | null;
  emailInvalid: boolean;
  onBack: () => void;
  headingRef: Ref<HTMLHeadingElement>;
  turnstileSiteKey?: string;
  turnstileError: string | null;
  turnstileRef: RefCallback<HTMLElement>;
}) {
  return (
    <form className="proof-lookup" onSubmit={(event) => { event.preventDefault(); onSubmit(); }} noValidate>
      <h1 ref={headingRef} tabIndex={-1} className="proof-title">Check your proof</h1>
      <p id="proof-email-help" className="proof-body">
        Enter the email address you submitted with. We'll email a private link to your pages.
      </p>

      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="proof-website">Website (leave blank)</label>
        <input id="proof-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} />
      </div>

      <label className="sr-only" htmlFor="proof-email">Email address</label>
      <input
        id="proof-email"
        name="email"
        className="proof-input"
        type="email"
        required
        autoComplete="email"
        inputMode="email"
        placeholder="you@example.com"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        aria-invalid={emailInvalid ? true : undefined}
        aria-describedby={emailInvalid ? "proof-email-help proof-email-error" : "proof-email-help"}
      />
      {turnstileSiteKey && (
        <div className="turnstile-wrap proof-turnstile">
          <div ref={turnstileRef} />
        </div>
      )}
      {turnstileError && <p className="proof-error" role="alert">{turnstileError}</p>}
      {error && <p id="proof-email-error" className="proof-error" role="alert">{error}</p>}
      <button type="submit" className="proof-btn proof-btn--ok">Email my private link</button>
      <button type="button" className="proof-link" onClick={onBack}>← Back</button>
    </form>
  );
}

export default ProofView;
